import type { ShopifyConversionsSummary } from '@discovr/types';
import { shopifyCopy } from '@discovr/features/copy';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import { fmtStoreDateTime } from '../campaign-detail/components/shopifyStoreTime';
import { InfoTooltip } from './InfoTooltip';

const activityCopy = shopifyCopy.report.activity;

function ActivityPart({
  label,
  none,
  at,
  tz,
}: {
  label: string;
  none: string;
  at: string | null;
  tz?: string;
}) {
  if (!at) return <span className="whitespace-nowrap">{none}</span>;
  return (
    <span className="whitespace-nowrap">
      {label} <span className="text-foreground">{fmtStoreDateTime(at, tz)}</span>
    </span>
  );
}

/** UX3: when Discovr last stored a visit and last applied an order update, on
 *  one line; what the times mean is one tap away. */
export function ShopifyRecordedActivity({
  summary,
  action,
  className,
  loadedAt,
}: {
  summary: ShopifyConversionsSummary;
  action?: React.ReactNode;
  className?: string;
  loadedAt?: number;
}) {
  return (
    <p
      className={cn(
        'text-muted-foreground mt-3 mb-5 flex flex-wrap items-center gap-x-4 gap-y-1 text-caption-regular',
        className,
      )}
    >
      <ActivityPart
        label={activityCopy.visit}
        none={activityCopy.visitNone}
        at={summary.last_referral_visit_at}
        tz={summary.store_timezone}
      />
      <span data-slot="activity-order">
        <span className="sr-only">; </span>
        <ActivityPart
          label={activityCopy.order}
          none={activityCopy.orderNone}
          at={summary.last_order_update_at}
          tz={summary.store_timezone}
        />
      </span>
      <InfoTooltip label={activityCopy.triggerLabel}>{activityCopy.hint}</InfoTooltip>
      {loadedAt ? (
        <span className="whitespace-nowrap">
          Last updated{' '}
          <span className="text-foreground">
            {fmtStoreDateTime(new Date(loadedAt).toISOString(), viewerZone())}
          </span>
        </span>
      ) : null}
      {action}
    </p>
  );
}

/** The viewer's own zone: last loaded is when this browser fetched. */
function viewerZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/** One table's refetch failed while the summary loaded: its rows stay on screen
 *  as of the last successful fetch. Cleared by the next successful read. */
export function TableRefreshFailedNotice({
  lastLoadedAt,
  retrying,
  onRetry,
}: RefreshFailedNoticeProps) {
  return (
    <Alert
      variant="warning"
      role="status"
      aria-live="polite"
      className="mb-2 flex flex-wrap items-center gap-3 py-2 text-caption-regular"
    >
      <span>
        Couldn't refresh this table
        {lastLoadedAt > 0
          ? ` · as of ${fmtStoreDateTime(new Date(lastLoadedAt).toISOString(), viewerZone())}`
          : ''}
      </span>
      <Button
        variant="outline"
        size="sm"
        className={cn('rounded-full', retrying && 'opacity-50')}
        aria-disabled={retrying}
        onClick={() => {
          if (!retrying) onRetry();
        }}
      >
        {retrying ? shopifyCopy.report.retrying : 'Retry'}
      </Button>
    </Alert>
  );
}

interface RefreshFailedNoticeProps {
  lastLoadedAt: number;
  retrying: boolean;
  onRetry: () => void;
}

/** ACT2: a background refresh failed, so the figures on screen are the last
 *  ones that loaded. Cleared by the next successful read. */
export function ShopifyRefreshFailedNotice({
  lastLoadedAt,
  retrying,
  onRetry,
}: RefreshFailedNoticeProps) {
  return (
    <Alert
      variant="warning"
      role="status"
      className="mb-4 flex flex-wrap items-center gap-3 py-2 text-caption-regular"
    >
      <span>
        {shopifyCopy.report.refreshFailed(
          lastLoadedAt > 0
            ? fmtStoreDateTime(new Date(lastLoadedAt).toISOString(), viewerZone())
            : undefined,
        )}
      </span>
      {/* aria-disabled keeps focus on the control while the retry runs. */}
      <Button
        variant="outline"
        size="sm"
        className={cn('rounded-full', retrying && 'opacity-50')}
        aria-disabled={retrying}
        onClick={() => {
          if (!retrying) onRetry();
        }}
      >
        {retrying ? shopifyCopy.report.retrying : shopifyCopy.report.retry}
      </Button>
    </Alert>
  );
}
